/**
 * Shared event taxonomy. Mirrors the Prisma `UserEventType` enum but is the
 * canonical source for non-prisma consumers (PostHog event names, dashboard
 * labels, agent tool descriptions).
 *
 * Events follow `noun_verb` snake_case so they read well in PostHog and HogQL.
 *
 * Categories follow AARRR (Acquisition / Activation / Retention / Referral /
 * Revenue).
 */

export type GrowthStage =
  | 'ACQUISITION'
  | 'ACTIVATION'
  | 'RETENTION'
  | 'REFERRAL'
  | 'REVENUE';

export const UserEventType = {
  // ─── Acquisition ──────────────────────────────────────────────────────────
  SIGN_UP_FORM_OPENED: 'SIGN_UP_FORM_OPENED',
  SIGNUP_STARTED: 'SIGNUP_STARTED',
  SIGNUP_VALIDATION_ERROR: 'SIGNUP_VALIDATION_ERROR',
  SIGNUP: 'SIGNUP',
  SIGNUP_FROM_TYPEFORM_IMPORTER: 'SIGNUP_FROM_TYPEFORM_IMPORTER',
  SIGN_UP_WITH_TEMPLATE: 'SIGN_UP_WITH_TEMPLATE',
  SIGN_IN_STARTED: 'SIGN_IN_STARTED',
  SIGN_IN_COMPLETED: 'SIGN_IN_COMPLETED',
  SIGN_IN_FAILED: 'SIGN_IN_FAILED',

  // ─── Activation ───────────────────────────────────────────────────────────
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  RESET_PASSWORD_REQUESTED: 'RESET_PASSWORD_REQUESTED',
  RESET_PASSWORD_SUCCESS: 'RESET_PASSWORD_SUCCESS',
  WORKSPACE_INVITE_ACCEPTED: 'WORKSPACE_INVITE_ACCEPTED',
  ONBOARDING_STEP_VIEWED: 'ONBOARDING_STEP_VIEWED',
  ONBOARDING_STEP_ANSWERED: 'ONBOARDING_STEP_ANSWERED',
  ONBOARDING_COMPLETED: 'ONBOARDING_COMPLETED',
  ONBOARDING_SKIPPED: 'ONBOARDING_SKIPPED',
  EDITOR_FIRST_ENTERED: 'EDITOR_FIRST_ENTERED',
  EDITOR_BOUNCED_WITHOUT_SAVE: 'EDITOR_BOUNCED_WITHOUT_SAVE',
  EDITOR_GET_STARTED_INTRO: 'EDITOR_GET_STARTED_INTRO',
  EDITOR_GET_STARTED_INTRO_SKIPPED: 'EDITOR_GET_STARTED_INTRO_SKIPPED',
  EDITOR_GET_STARTED_INTRO_VIDEO_VIEWED: 'EDITOR_GET_STARTED_INTRO_VIDEO_VIEWED',
  HOW_TO_TOOLTIP_OPENED: 'HOW_TO_TOOLTIP_OPENED',
  HOW_TO_TOOLTIP_VIDEO_VIEWED: 'HOW_TO_TOOLTIP_VIDEO_VIEWED',
  FORM_CREATED: 'FORM_CREATED',
  FORM_DRAFT_SAVED: 'FORM_DRAFT_SAVED',
  FORM_PUBLISHED: 'FORM_PUBLISHED',
  FORM_FIRST_PUBLISHED: 'FORM_FIRST_PUBLISHED',
  ADD_JOURNEY_ITEM: 'ADD_JOURNEY_ITEM',
  EDIT_JOURNEY_ITEM: 'EDIT_JOURNEY_ITEM',
  QUESTION_EDITED: 'QUESTION_EDITED',
  OPEN_AI_ASSISTANT_MODAL: 'OPEN_AI_ASSISTANT_MODAL',
  CREATE_FORM_WITH_AI_OPENED: 'CREATE_FORM_WITH_AI_OPENED',
  CREATE_FORM_WITH_AI_SUBMITTED: 'CREATE_FORM_WITH_AI_SUBMITTED',
  OPEN_SHARE_FORM_MODAL: 'OPEN_SHARE_FORM_MODAL',
  ADD_TEMPLATE_TO_WORKSPACE: 'ADD_TEMPLATE_TO_WORKSPACE',
  PREVIEW_TEMPLATE: 'PREVIEW_TEMPLATE',
  CREATE_FORM_FROM_TYPEFORM: 'CREATE_FORM_FROM_TYPEFORM',
  ADD_TYPEFORM_TO_WORKSPACE: 'ADD_TYPEFORM_TO_WORKSPACE',
  FIRST_RESPONSE_RECEIVED: 'FIRST_RESPONSE_RECEIVED',
  /// Fired server-side by `finalizeConversation` when a workspace crosses a
  /// response-count milestone. `metadata = { count }` (currently only 5).
  RESPONSE_MILESTONE_REACHED: 'RESPONSE_MILESTONE_REACHED',

  // ─── Webhooks (server-side, activation) ───────────────────────────────────
  WEBHOOK_CREATED: 'WEBHOOK_CREATED',
  WEBHOOK_DELIVERY_SUCCEEDED: 'WEBHOOK_DELIVERY_SUCCEEDED',
  WEBHOOK_DELIVERY_FAILED: 'WEBHOOK_DELIVERY_FAILED',
  WEBHOOK_AUTO_DISABLED: 'WEBHOOK_AUTO_DISABLED',

  // ─── Retention ────────────────────────────────────────────────────────────
  SESSION_STARTED: 'SESSION_STARTED',
  RETURNED_AFTER_INACTIVITY: 'RETURNED_AFTER_INACTIVITY',
  /// Feedback Program lifecycle: INVITED fires when the admin panel sends an
  /// invite; JOINED fires when the user accepts on /feedback-program.
  FEEDBACK_PROGRAM_INVITED: 'FEEDBACK_PROGRAM_INVITED',
  FEEDBACK_PROGRAM_JOINED: 'FEEDBACK_PROGRAM_JOINED',
  AI_CHAT_STARTED: 'AI_CHAT_STARTED',
  AI_CHAT_CHANGES_APPLIED: 'AI_CHAT_CHANGES_APPLIED',
  AI_CHAT_CHANGES_DISCARDED: 'AI_CHAT_CHANGES_DISCARDED',
  FORM_EDITED_AFTER_PUBLISH: 'FORM_EDITED_AFTER_PUBLISH',
  REACTIVATION_EMAIL_OPENED: 'REACTIVATION_EMAIL_OPENED',
  REACTIVATION_EMAIL_CLICKED: 'REACTIVATION_EMAIL_CLICKED',

  // ─── Referral ─────────────────────────────────────────────────────────────
  REFERRAL_LINK_COPIED: 'REFERRAL_LINK_COPIED',
  REFERRAL_LINK_SHARED: 'REFERRAL_LINK_SHARED',
  REFERRAL_LEAD_CREATED: 'REFERRAL_LEAD_CREATED',
  REFERRAL_SALE_COMPLETED: 'REFERRAL_SALE_COMPLETED',
  BUILT_WITH_BADGE_CLICKED: 'BUILT_WITH_BADGE_CLICKED',

  // ─── Revenue ──────────────────────────────────────────────────────────────
  OPEN_BILLING_MODAL: 'OPEN_BILLING_MODAL',
  PRICING_PAGE_VIEWED: 'PRICING_PAGE_VIEWED',
  PAYWALL_SHOWN: 'PAYWALL_SHOWN',
  PAYWALL_UPGRADE_CLICKED: 'PAYWALL_UPGRADE_CLICKED',
  AI_CHAT_LIMIT_REACHED: 'AI_CHAT_LIMIT_REACHED',
  AI_CHAT_UPGRADE_CLICKED: 'AI_CHAT_UPGRADE_CLICKED',
  SUBSCRIPTION_STARTED: 'SUBSCRIPTION_STARTED',
  UPGRADE_NUDGE_EMAIL_SENT: 'UPGRADE_NUDGE_EMAIL_SENT',
  SUBSCRIPTION_CANCELLED: 'SUBSCRIPTION_CANCELLED',
  SUBSCRIPTION_REACTIVATED: 'SUBSCRIPTION_REACTIVATED',
  FEEDBACK_SURVEY_SUBMITTED: 'FEEDBACK_SURVEY_SUBMITTED',

  // ─── Acquisition (added) ──────────────────────────────────────────────────
  LANDING_PAGE_VIEWED: 'LANDING_PAGE_VIEWED',
  LANDING_CTA_CLICKED: 'LANDING_CTA_CLICKED',
  PRICING_FAQ_OPENED: 'PRICING_FAQ_OPENED',
  BLOG_ARTICLE_VIEWED: 'BLOG_ARTICLE_VIEWED',
  BLOG_CTA_CLICKED: 'BLOG_CTA_CLICKED',
  DEMO_FORM_INTERACTED: 'DEMO_FORM_INTERACTED',
  SIGNUP_SOCIAL_PROVIDER_SELECTED: 'SIGNUP_SOCIAL_PROVIDER_SELECTED',

  // ─── Activation (added) ───────────────────────────────────────────────────
  TEMPLATE_BROWSE_OPENED: 'TEMPLATE_BROWSE_OPENED',
  TEMPLATE_SELECTED: 'TEMPLATE_SELECTED',
  STARTED_FROM_SCRATCH: 'STARTED_FROM_SCRATCH',
  EDITOR_FIRST_FIELD_ADDED: 'EDITOR_FIRST_FIELD_ADDED',
  EDITOR_BOUNCED: 'EDITOR_BOUNCED',
  EDITOR_LOGIC_OPENED: 'EDITOR_LOGIC_OPENED',
  EDITOR_PREVIEWED: 'EDITOR_PREVIEWED',
  FORM_SHARED_VIA_LINK: 'FORM_SHARED_VIA_LINK',
  FORM_EMBED_CODE_COPIED: 'FORM_EMBED_CODE_COPIED',

  // ─── Retention (added) ────────────────────────────────────────────────────
  DASHBOARD_VIEWED: 'DASHBOARD_VIEWED',
  RESPONSES_VIEWED: 'RESPONSES_VIEWED',
  REPORTS_TAB_VIEWED: 'REPORTS_TAB_VIEWED',
  INTEGRATION_VIEWED: 'INTEGRATION_VIEWED',
  INTEGRATION_CONNECTED: 'INTEGRATION_CONNECTED',
  WORKSPACE_INVITE_SENT: 'WORKSPACE_INVITE_SENT',

  // ─── Help Widget (Retention) ──────────────────────────────────────────────
  HELP_WIDGET_OPENED: 'HELP_WIDGET_OPENED',
  HELP_WIDGET_MENU_ITEM_CLICKED: 'HELP_WIDGET_MENU_ITEM_CLICKED',
  HELP_WIDGET_UPDATE_VIEWED: 'HELP_WIDGET_UPDATE_VIEWED',

  // ─── Revenue (added) ──────────────────────────────────────────────────────
  PAID_FEATURE_HOVERED: 'PAID_FEATURE_HOVERED',
  PAID_FEATURE_GATED_VIEWED: 'PAID_FEATURE_GATED_VIEWED',
  UPGRADE_CTA_CLICKED: 'UPGRADE_CTA_CLICKED',
  CHECKOUT_STARTED: 'CHECKOUT_STARTED',
  CHECKOUT_ABANDONED: 'CHECKOUT_ABANDONED',
  PLAN_SWITCHED: 'PLAN_SWITCHED',
  CANCEL_FLOW_STARTED: 'CANCEL_FLOW_STARTED',
  CANCEL_REASON_SUBMITTED: 'CANCEL_REASON_SUBMITTED',
  WIN_BACK_OFFER_VIEWED: 'WIN_BACK_OFFER_VIEWED',
  WIN_BACK_OFFER_REDEEMED: 'WIN_BACK_OFFER_REDEEMED',
  WIN_BACK_EMAIL_OPENED: 'WIN_BACK_EMAIL_OPENED',
  WIN_BACK_EMAIL_CLICKED: 'WIN_BACK_EMAIL_CLICKED',
  SAVE_OFFER_ACCEPTED: 'SAVE_OFFER_ACCEPTED',
  NPS_PROMPT_SHOWN: 'NPS_PROMPT_SHOWN',
  NPS_PROMPT_SUBMITTED: 'NPS_PROMPT_SUBMITTED',
  /// LEGACY: first-form-published upsell modal (retired — the surface moved
  /// to the first-response moment, see FIRST_RESPONSE_UPSELL_* below).
  /// Kept for historical event data.
  FIRST_FORM_UPSELL_FEEDBACK_STARTED: 'FIRST_FORM_UPSELL_FEEDBACK_STARTED',
  FIRST_FORM_UPSELL_DISMISSED: 'FIRST_FORM_UPSELL_DISMISSED',
  /// First-response upsell modal (`FirstResponseUpsellModal`). Successor to
  /// the first-form upsell: shows once, when a free user's workspace gets its
  /// first completed response. SHOWN doubles as the one-shot eligibility
  /// gate. PAYWALL_SHOWN / UPGRADE_CTA_CLICKED tag the same surface with
  /// `source: 'first_response_upsell'`.
  FIRST_RESPONSE_UPSELL_SHOWN: 'FIRST_RESPONSE_UPSELL_SHOWN',
  FIRST_RESPONSE_UPSELL_FEEDBACK_STARTED: 'FIRST_RESPONSE_UPSELL_FEEDBACK_STARTED',
  FIRST_RESPONSE_UPSELL_DISMISSED: 'FIRST_RESPONSE_UPSELL_DISMISSED',

  // ─── Friction (added) ─────────────────────────────────────────────────────
  RAGE_CLICK: 'RAGE_CLICK',
  DEAD_CLICK: 'DEAD_CLICK',

  // ─── Experiments (added) ──────────────────────────────────────────────────
  /// Fires once per `(userId, slot)` when the user first sees an
  /// experiment slot rendered. Drives the denominator of conversion-rate
  /// stats. `metadata = { slot, variant }`.
  EXPERIMENT_VIEWED: 'EXPERIMENT_VIEWED',
  /// Fires when the user takes the slot's primary action (click on the
  /// CTA, complete the gated step, etc.). The numerator. `metadata =
  /// { slot, variant }`. The slot's primary metric is whatever event the
  /// slot registry says it is — this generic event is just a redundant
  /// roll-up so the agent can find every conversion in one query.
  EXPERIMENT_CONVERTED: 'EXPERIMENT_CONVERTED',
} as const;

export type UserEventType = (typeof UserEventType)[keyof typeof UserEventType];

/**
 * Mapping from internal `UserEventType` enum value to the canonical
 * snake_case PostHog event name. Same value, lowercased.
 */
export function userEventTypeToPosthog(name: UserEventType): string {
  return name.toLowerCase();
}

/**
 * AARRR stage classifier for every event. Used by the agents to filter
 * relevant events per stage and by the dashboard to colour-code things.
 */
export const EventStage: Record<UserEventType, GrowthStage> = {
  // Acquisition
  SIGN_UP_FORM_OPENED: 'ACQUISITION',
  SIGNUP_STARTED: 'ACQUISITION',
  SIGNUP_VALIDATION_ERROR: 'ACQUISITION',
  SIGNUP: 'ACQUISITION',
  SIGNUP_FROM_TYPEFORM_IMPORTER: 'ACQUISITION',
  SIGN_UP_WITH_TEMPLATE: 'ACQUISITION',
  SIGN_IN_STARTED: 'ACQUISITION',
  SIGN_IN_COMPLETED: 'ACQUISITION',
  SIGN_IN_FAILED: 'ACQUISITION',

  // Activation
  EMAIL_VERIFIED: 'ACTIVATION',
  RESET_PASSWORD_REQUESTED: 'ACTIVATION',
  RESET_PASSWORD_SUCCESS: 'ACTIVATION',
  WORKSPACE_INVITE_ACCEPTED: 'ACTIVATION',
  ONBOARDING_STEP_VIEWED: 'ACTIVATION',
  ONBOARDING_STEP_ANSWERED: 'ACTIVATION',
  ONBOARDING_COMPLETED: 'ACTIVATION',
  ONBOARDING_SKIPPED: 'ACTIVATION',
  EDITOR_FIRST_ENTERED: 'ACTIVATION',
  EDITOR_BOUNCED_WITHOUT_SAVE: 'ACTIVATION',
  EDITOR_GET_STARTED_INTRO: 'ACTIVATION',
  EDITOR_GET_STARTED_INTRO_SKIPPED: 'ACTIVATION',
  EDITOR_GET_STARTED_INTRO_VIDEO_VIEWED: 'ACTIVATION',
  HOW_TO_TOOLTIP_OPENED: 'ACTIVATION',
  HOW_TO_TOOLTIP_VIDEO_VIEWED: 'ACTIVATION',
  FORM_CREATED: 'ACTIVATION',
  FORM_DRAFT_SAVED: 'ACTIVATION',
  FORM_PUBLISHED: 'ACTIVATION',
  FORM_FIRST_PUBLISHED: 'ACTIVATION',
  ADD_JOURNEY_ITEM: 'ACTIVATION',
  EDIT_JOURNEY_ITEM: 'ACTIVATION',
  QUESTION_EDITED: 'ACTIVATION',
  OPEN_AI_ASSISTANT_MODAL: 'ACTIVATION',
  CREATE_FORM_WITH_AI_OPENED: 'ACTIVATION',
  CREATE_FORM_WITH_AI_SUBMITTED: 'ACTIVATION',
  OPEN_SHARE_FORM_MODAL: 'ACTIVATION',
  ADD_TEMPLATE_TO_WORKSPACE: 'ACTIVATION',
  PREVIEW_TEMPLATE: 'ACTIVATION',
  CREATE_FORM_FROM_TYPEFORM: 'ACTIVATION',
  ADD_TYPEFORM_TO_WORKSPACE: 'ACTIVATION',
  FIRST_RESPONSE_RECEIVED: 'ACTIVATION',
  RESPONSE_MILESTONE_REACHED: 'ACTIVATION',
  WEBHOOK_CREATED: 'ACTIVATION',
  WEBHOOK_DELIVERY_SUCCEEDED: 'RETENTION',
  WEBHOOK_DELIVERY_FAILED: 'RETENTION',
  WEBHOOK_AUTO_DISABLED: 'RETENTION',

  // Retention
  SESSION_STARTED: 'RETENTION',
  RETURNED_AFTER_INACTIVITY: 'RETENTION',
  FEEDBACK_PROGRAM_INVITED: 'RETENTION',
  FEEDBACK_PROGRAM_JOINED: 'RETENTION',
  AI_CHAT_STARTED: 'RETENTION',
  AI_CHAT_CHANGES_APPLIED: 'RETENTION',
  AI_CHAT_CHANGES_DISCARDED: 'RETENTION',
  FORM_EDITED_AFTER_PUBLISH: 'RETENTION',
  REACTIVATION_EMAIL_OPENED: 'RETENTION',
  REACTIVATION_EMAIL_CLICKED: 'RETENTION',

  // Referral
  REFERRAL_LINK_COPIED: 'REFERRAL',
  REFERRAL_LINK_SHARED: 'REFERRAL',
  REFERRAL_LEAD_CREATED: 'REFERRAL',
  REFERRAL_SALE_COMPLETED: 'REFERRAL',
  BUILT_WITH_BADGE_CLICKED: 'REFERRAL',

  // Revenue
  OPEN_BILLING_MODAL: 'REVENUE',
  PRICING_PAGE_VIEWED: 'REVENUE',
  PAYWALL_SHOWN: 'REVENUE',
  PAYWALL_UPGRADE_CLICKED: 'REVENUE',
  AI_CHAT_LIMIT_REACHED: 'REVENUE',
  AI_CHAT_UPGRADE_CLICKED: 'REVENUE',
  SUBSCRIPTION_STARTED: 'REVENUE',
  UPGRADE_NUDGE_EMAIL_SENT: 'REVENUE',
  SUBSCRIPTION_CANCELLED: 'REVENUE',
  SUBSCRIPTION_REACTIVATED: 'REVENUE',
  FEEDBACK_SURVEY_SUBMITTED: 'REVENUE',

  // Acquisition (added)
  LANDING_PAGE_VIEWED: 'ACQUISITION',
  LANDING_CTA_CLICKED: 'ACQUISITION',
  PRICING_FAQ_OPENED: 'ACQUISITION',
  BLOG_ARTICLE_VIEWED: 'ACQUISITION',
  BLOG_CTA_CLICKED: 'ACQUISITION',
  DEMO_FORM_INTERACTED: 'ACQUISITION',
  SIGNUP_SOCIAL_PROVIDER_SELECTED: 'ACQUISITION',

  // Activation (added)
  TEMPLATE_BROWSE_OPENED: 'ACTIVATION',
  TEMPLATE_SELECTED: 'ACTIVATION',
  STARTED_FROM_SCRATCH: 'ACTIVATION',
  EDITOR_FIRST_FIELD_ADDED: 'ACTIVATION',
  EDITOR_BOUNCED: 'ACTIVATION',
  EDITOR_LOGIC_OPENED: 'ACTIVATION',
  EDITOR_PREVIEWED: 'ACTIVATION',
  FORM_SHARED_VIA_LINK: 'ACTIVATION',
  FORM_EMBED_CODE_COPIED: 'ACTIVATION',

  // Retention (added)
  DASHBOARD_VIEWED: 'RETENTION',
  RESPONSES_VIEWED: 'RETENTION',
  REPORTS_TAB_VIEWED: 'RETENTION',
  INTEGRATION_VIEWED: 'RETENTION',
  INTEGRATION_CONNECTED: 'RETENTION',
  WORKSPACE_INVITE_SENT: 'RETENTION',

  // Help Widget
  HELP_WIDGET_OPENED: 'RETENTION',
  HELP_WIDGET_MENU_ITEM_CLICKED: 'RETENTION',
  HELP_WIDGET_UPDATE_VIEWED: 'RETENTION',

  // Revenue (added)
  PAID_FEATURE_HOVERED: 'REVENUE',
  PAID_FEATURE_GATED_VIEWED: 'REVENUE',
  UPGRADE_CTA_CLICKED: 'REVENUE',
  CHECKOUT_STARTED: 'REVENUE',
  CHECKOUT_ABANDONED: 'REVENUE',
  PLAN_SWITCHED: 'REVENUE',
  CANCEL_FLOW_STARTED: 'REVENUE',
  CANCEL_REASON_SUBMITTED: 'REVENUE',
  WIN_BACK_OFFER_VIEWED: 'REVENUE',
  WIN_BACK_OFFER_REDEEMED: 'REVENUE',
  WIN_BACK_EMAIL_OPENED: 'REVENUE',
  WIN_BACK_EMAIL_CLICKED: 'REVENUE',
  SAVE_OFFER_ACCEPTED: 'REVENUE',
  NPS_PROMPT_SHOWN: 'REVENUE',
  NPS_PROMPT_SUBMITTED: 'REVENUE',
  FIRST_FORM_UPSELL_FEEDBACK_STARTED: 'REVENUE',
  FIRST_FORM_UPSELL_DISMISSED: 'REVENUE',
  FIRST_RESPONSE_UPSELL_SHOWN: 'REVENUE',
  FIRST_RESPONSE_UPSELL_FEEDBACK_STARTED: 'REVENUE',
  FIRST_RESPONSE_UPSELL_DISMISSED: 'REVENUE',

  // Friction
  RAGE_CLICK: 'ACTIVATION',
  DEAD_CLICK: 'ACTIVATION',

  // Experiments — stage is set by the slot at runtime via metadata; default
  // here is ACTIVATION since most slots target activation. The agent reads
  // the slot definition for the real stage when computing winners.
  EXPERIMENT_VIEWED: 'ACTIVATION',
  EXPERIMENT_CONVERTED: 'ACTIVATION',
};

/**
 * Generic event metadata bag. We intentionally don't enforce a per-event
 * schema at compile time anymore — the new `track()` helper is permissive and
 * the marketing/bizdev agents can correlate properties at query time. If you
 * need strong typing for a specific call site, define a local helper in your
 * feature directory.
 */
export type TrackEventInput<T extends UserEventType = UserEventType> = {
  eventName: T;
  workspaceId?: string;
  metadata?: Record<string, unknown>;
};

export const EventLabels: Record<UserEventType, string> = {
  SIGN_UP_FORM_OPENED: 'Opened Sign Up Form',
  SIGNUP_STARTED: 'Started Sign Up',
  SIGNUP_VALIDATION_ERROR: 'Sign Up Validation Error',
  SIGNUP: 'Signed Up',
  SIGNUP_FROM_TYPEFORM_IMPORTER: 'Signed Up from Typeform Import',
  SIGN_UP_WITH_TEMPLATE: 'Signed Up with Template',
  SIGN_IN_STARTED: 'Started Sign In',
  SIGN_IN_COMPLETED: 'Signed In',
  SIGN_IN_FAILED: 'Sign In Failed',
  EMAIL_VERIFIED: 'Verified Email',
  RESET_PASSWORD_REQUESTED: 'Requested Password Reset',
  RESET_PASSWORD_SUCCESS: 'Reset Password',
  WORKSPACE_INVITE_ACCEPTED: 'Accepted Workspace Invite',
  ONBOARDING_STEP_VIEWED: 'Viewed Onboarding Step',
  ONBOARDING_STEP_ANSWERED: 'Answered Onboarding Step',
  ONBOARDING_COMPLETED: 'Completed Onboarding',
  ONBOARDING_SKIPPED: 'Skipped Onboarding Quick-Start',
  EDITOR_FIRST_ENTERED: 'Entered Editor (First Time)',
  EDITOR_BOUNCED_WITHOUT_SAVE: 'Bounced from Editor without Saving',
  EDITOR_GET_STARTED_INTRO: 'Viewed Editor Intro',
  EDITOR_GET_STARTED_INTRO_SKIPPED: 'Skipped Editor Intro',
  EDITOR_GET_STARTED_INTRO_VIDEO_VIEWED: 'Watched Editor Video',
  HOW_TO_TOOLTIP_OPENED: 'Opened How-To Tooltip',
  HOW_TO_TOOLTIP_VIDEO_VIEWED: 'Watched How-To Video',
  FORM_CREATED: 'Created Form',
  FORM_DRAFT_SAVED: 'Saved Draft',
  FORM_PUBLISHED: 'Published Form',
  FORM_FIRST_PUBLISHED: 'Published First Form',
  ADD_JOURNEY_ITEM: 'Added Question',
  EDIT_JOURNEY_ITEM: 'Selected Question',
  QUESTION_EDITED: 'Edited Question',
  OPEN_AI_ASSISTANT_MODAL: 'Opened AI Assistant',
  CREATE_FORM_WITH_AI_OPENED: 'Opened AI Form Builder',
  CREATE_FORM_WITH_AI_SUBMITTED: 'Created Form with AI',
  OPEN_SHARE_FORM_MODAL: 'Opened Share Modal',
  ADD_TEMPLATE_TO_WORKSPACE: 'Added Template',
  PREVIEW_TEMPLATE: 'Previewed Template',
  CREATE_FORM_FROM_TYPEFORM: 'Imported from Typeform',
  ADD_TYPEFORM_TO_WORKSPACE: 'Added Typeform to Workspace',
  FIRST_RESPONSE_RECEIVED: 'Got First Response',
  RESPONSE_MILESTONE_REACHED: 'Reached Response Milestone',
  WEBHOOK_CREATED: 'Created Webhook',
  WEBHOOK_DELIVERY_SUCCEEDED: 'Webhook Delivered',
  WEBHOOK_DELIVERY_FAILED: 'Webhook Failed',
  WEBHOOK_AUTO_DISABLED: 'Webhook Auto-Disabled',
  SESSION_STARTED: 'Started Session',
  RETURNED_AFTER_INACTIVITY: 'Returned After Inactivity',
  FEEDBACK_PROGRAM_INVITED: 'Invited to Feedback Program',
  FEEDBACK_PROGRAM_JOINED: 'Joined Feedback Program',
  AI_CHAT_STARTED: 'Started AI Chat',
  AI_CHAT_CHANGES_APPLIED: 'Applied AI Chat Changes',
  AI_CHAT_CHANGES_DISCARDED: 'Discarded AI Chat Changes',
  FORM_EDITED_AFTER_PUBLISH: 'Edited Form After Publish',
  REACTIVATION_EMAIL_OPENED: 'Opened Reactivation Email',
  REACTIVATION_EMAIL_CLICKED: 'Clicked Reactivation Email',
  REFERRAL_LINK_COPIED: 'Copied Referral Link',
  REFERRAL_LINK_SHARED: 'Shared Referral Link',
  REFERRAL_LEAD_CREATED: 'Created Referral Lead',
  REFERRAL_SALE_COMPLETED: 'Completed Referral Sale',
  BUILT_WITH_BADGE_CLICKED: 'Clicked "Built with Formsuite" Badge',
  OPEN_BILLING_MODAL: 'Opened Billing',
  PRICING_PAGE_VIEWED: 'Viewed Pricing Page',
  PAYWALL_SHOWN: 'Shown Paywall',
  PAYWALL_UPGRADE_CLICKED: 'Clicked Paywall Upgrade',
  AI_CHAT_LIMIT_REACHED: 'Hit AI Chat Limit',
  AI_CHAT_UPGRADE_CLICKED: 'Clicked Upgrade from AI Chat',
  SUBSCRIPTION_STARTED: 'Started Subscription',
  UPGRADE_NUDGE_EMAIL_SENT: 'Sent Upgrade Nudge Email',
  SUBSCRIPTION_CANCELLED: 'Cancelled Subscription',
  SUBSCRIPTION_REACTIVATED: 'Reactivated Subscription',
  FEEDBACK_SURVEY_SUBMITTED: 'Submitted Feedback Survey',

  LANDING_PAGE_VIEWED: 'Viewed Landing Page',
  LANDING_CTA_CLICKED: 'Clicked Landing CTA',
  PRICING_FAQ_OPENED: 'Opened Pricing FAQ',
  BLOG_ARTICLE_VIEWED: 'Viewed Blog Article',
  BLOG_CTA_CLICKED: 'Clicked Blog CTA',
  DEMO_FORM_INTERACTED: 'Interacted with Demo Form',
  SIGNUP_SOCIAL_PROVIDER_SELECTED: 'Selected Social Provider on Sign Up',

  TEMPLATE_BROWSE_OPENED: 'Browsed Templates',
  TEMPLATE_SELECTED: 'Selected Template',
  STARTED_FROM_SCRATCH: 'Started Form from Scratch',
  EDITOR_FIRST_FIELD_ADDED: 'Added First Field',
  EDITOR_BOUNCED: 'Bounced from Editor',
  EDITOR_LOGIC_OPENED: 'Opened Editor Logic',
  EDITOR_PREVIEWED: 'Previewed Form in Editor',
  FORM_SHARED_VIA_LINK: 'Shared Form via Link',
  FORM_EMBED_CODE_COPIED: 'Copied Form Embed Code',

  DASHBOARD_VIEWED: 'Viewed Dashboard',
  RESPONSES_VIEWED: 'Viewed Responses',
  REPORTS_TAB_VIEWED: 'Viewed Reports Tab',
  INTEGRATION_VIEWED: 'Viewed Integration',
  INTEGRATION_CONNECTED: 'Connected Integration',
  WORKSPACE_INVITE_SENT: 'Sent Workspace Invite',

  HELP_WIDGET_OPENED: 'Opened Help Widget',
  HELP_WIDGET_MENU_ITEM_CLICKED: 'Clicked Help Widget Menu Item',
  HELP_WIDGET_UPDATE_VIEWED: 'Viewed Feature Update',

  PAID_FEATURE_HOVERED: 'Hovered Paid Feature',
  PAID_FEATURE_GATED_VIEWED: 'Viewed Gated Paid Feature',
  UPGRADE_CTA_CLICKED: 'Clicked Upgrade CTA',
  CHECKOUT_STARTED: 'Started Checkout',
  CHECKOUT_ABANDONED: 'Abandoned Checkout',
  PLAN_SWITCHED: 'Switched Plan',
  CANCEL_FLOW_STARTED: 'Started Cancel Flow',
  CANCEL_REASON_SUBMITTED: 'Submitted Cancel Reason',
  WIN_BACK_OFFER_VIEWED: 'Viewed Win-Back Offer',
  WIN_BACK_OFFER_REDEEMED: 'Redeemed Win-Back Offer',
  WIN_BACK_EMAIL_OPENED: 'Opened Win-Back Email',
  WIN_BACK_EMAIL_CLICKED: 'Clicked Win-Back Email',
  SAVE_OFFER_ACCEPTED: 'Accepted Save Offer',
  NPS_PROMPT_SHOWN: 'Shown NPS Prompt',
  NPS_PROMPT_SUBMITTED: 'Submitted NPS Prompt',
  FIRST_FORM_UPSELL_FEEDBACK_STARTED: 'Started First-Form Upsell Feedback',
  FIRST_FORM_UPSELL_DISMISSED: 'Dismissed First-Form Upsell',
  FIRST_RESPONSE_UPSELL_SHOWN: 'Shown First-Response Upsell',
  FIRST_RESPONSE_UPSELL_FEEDBACK_STARTED: 'Started First-Response Upsell Feedback',
  FIRST_RESPONSE_UPSELL_DISMISSED: 'Dismissed First-Response Upsell',

  RAGE_CLICK: 'Rage Click',
  DEAD_CLICK: 'Dead Click',

  EXPERIMENT_VIEWED: 'Experiment Slot Viewed',
  EXPERIMENT_CONVERTED: 'Experiment Slot Converted',
};

/**
 * Group events by AARRR stage for the admin UI.
 */
export function eventsByStage(): Record<GrowthStage, UserEventType[]> {
  const out: Record<GrowthStage, UserEventType[]> = {
    ACQUISITION: [],
    ACTIVATION: [],
    RETENTION: [],
    REFERRAL: [],
    REVENUE: [],
  };
  for (const name of Object.values(UserEventType)) {
    out[EventStage[name]].push(name);
  }
  return out;
}
